import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Checkbox from '@atlaskit/checkbox';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Pressable, Stack, xcss, Text, Flex, Box } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useContentType } from '@confluence/page-context';
import { ShortcutListener } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { GeneralAccessLevel, NatureOfCta } from '../../commonTypes.tsx';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller.tsx';
import type { ShareeAction } from '../../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator.tsx';
import { MessageComposer, type MessageComposerProps } from '../footer/MessageComposer.tsx';

export const i18n = defineMessages({
	notifyNewlyAddedUsersLabel: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.notify-newly-added-user-label',
		defaultMessage: 'Notify newly added people',
		description: 'A label for a checkbox to inform users that newly added users will be notified.',
	},
	notifyRecipientsLabel: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.notify-recipients-label',
		defaultMessage: 'Notify recipients',
		description: 'A label for a checkbox to inform users that recipients will be notified.',
	},
	//TODO: clean up this label is no longer needed
	notifySelectedUsersLabel: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.notify-selected-users-label',
		defaultMessage: 'Notify selected users',
		description: 'A label for a checkbox to inform users that selected users will be notified.',
	},
	addMessage: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.add-message',
		defaultMessage: 'Add message',
		description: 'A label for a button that, when clicked, reveals a dropdown text area.',
	},
	cancelButton: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.cancel-cta',
		defaultMessage: 'Cancel',
		description: 'Button text for the cancel button for share and restrict dialog',
	},
	shareButton: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.share-cta',
		defaultMessage: 'Share',
		description: 'Button text for the share button for share and restrict dialog',
	},
	saveButton: {
		id: 'share-and-restrict-dialog.cta-notify-message-footer.save-cta',
		defaultMessage: 'Save',
		description: 'Button text for the Save button for share and restrict dialog',
	},
});

type CheckboxLabelType = 'newly-added' | 'recipients' | 'selected-users' | 'notify-them';

export type CtaNotifyMessageProps = {
	persistenceMode: 'staged' | 'live';
	shouldShowCtaButtons: boolean;
	natureOfCta: NatureOfCta;
	notifyCheckbox: 'hidden' | 'unchecked' | 'checked';
	checkboxLabel: CheckboxLabelType;
	isLockedOut: boolean;
	isCtaInProgress: boolean;
	messageComposer: MessageComposerProps | undefined;
	sharingType: 'sharing-and-applying-restrictions' | 'applying-restrictions' | 'sharing' | null;
	isSpecificAccessDetailsVisible: boolean;
	onCtaClick: () => void;
	onCancelClick: () => void;
	onNotifyCheckboxClick: (isChecked: boolean) => void;
	onNotifyAddMessageClick: () => void;
	isLimitExceeded: boolean;
	pageMode: ShareeAction;
	isDraft: boolean;
	isLivePage: boolean;
	isSharingToEmailsDisabled: boolean;
	contentId: string;
	hasGroups: boolean;
	hasTeams: boolean;
	hasUsers: boolean;
	includeInvites: boolean;
	generalAccessLevel: GeneralAccessLevel;
	principalsCount: number;
};

type NotifyCheckboxComponentProp = {
	isLockedOut: CtaNotifyMessageProps['isLockedOut'];
	notifyCheckbox: Exclude<CtaNotifyMessageProps['notifyCheckbox'], 'hidden'>;
	checkboxLabel: CheckboxLabelType;
	showMessageComposer: boolean;
	onNotifyCheckboxClick: CtaNotifyMessageProps['onNotifyCheckboxClick'];
	onNotifyAddMessageClick: CtaNotifyMessageProps['onNotifyAddMessageClick'];
	isSharingToEmailsDisabled: boolean;
};

const containerBaseStyling = xcss({
	padding: 'space.300',
});

const withButtonsAdjustments = xcss({
	paddingTop: 'space.200',
	maxHeight: '72px',
});

const withButtonsCheckboxAdjustments = xcss({
	paddingTop: 'space.200',
	maxHeight: '72px',
});

const withButtonsCheckboxComposerAdjustments = xcss({
	paddingTop: 'space.075',
});

const withPSAButtonsCheckboxComposerAdjustments = xcss({
	padding: 'space.0',
});

const withButtonsSpecificAccessDetails = xcss({
	paddingTop: 'space.300',
	maxHeight: '80px',
});

const withButtonsCheckboxAndSpecificAccessDetails = xcss({
	paddingTop: 'space.300',
	minHeight: '80px',
});

const withButtonsCheckboxComposerAndSpecificAccessDetails = xcss({
	marginTop: 'space.025',
	paddingTop: 'space.150',
});

const addMessageButtonStyling = xcss({
	width: 'fit-content',
	height: '20px',
	border: 'none',
	backgroundColor: 'color.background.neutral.subtle',
	marginLeft: 'space.300',
	padding: '0',
	paddingInlineStart: 'space.050',
	position: 'relative',
	top: 'space.negative.050',
	flexShrink: 0,
});

const iconContainer = xcss({
	paddingInlineStart: 'space.025',
});

const ctaButtonContainerStylingWithMessageComposer = xcss({
	alignSelf: 'flex-end',
});

const spotLightContainer = xcss({
	padding: 'space.075',
	position: 'relative',
	top: 'space.025',
	left: 'space.negative.075',
	boxSizing: 'content-box',
	height: '40px',
});

const psaSpotLightContainer = xcss({
	height: 'auto',
});

const NotifyCheckboxComponent = ({
	isLockedOut,
	notifyCheckbox,
	checkboxLabel,
	showMessageComposer,
	onNotifyCheckboxClick,
	onNotifyAddMessageClick,
	isSharingToEmailsDisabled,
}: NotifyCheckboxComponentProp) => {
	const isChecked = notifyCheckbox === 'checked';
	const { formatMessage } = useIntl();
	const shouldExpandIcon = isChecked && showMessageComposer;

	const getCheckboxLabel = (label: CheckboxLabelType) => {
		switch (label) {
			case 'newly-added':
				return i18n.notifyNewlyAddedUsersLabel;
			case 'recipients':
				return i18n.notifyRecipientsLabel;
			case 'selected-users':
			default:
				return i18n.notifySelectedUsersLabel;
		}
	};

	const getChevronIcon = () => {
		if (shouldExpandIcon) {
			return <ChevronUpIcon label="" color={token('color.icon.subtle')} size="small" />;
		}

		if (!shouldExpandIcon) {
			return <ChevronDownIcon label="" color={token('color.icon.subtle')} size="small" />;
		}

		return null;
	};

	return (
		<Stack
			alignBlock="center"
			xcss={[
				spotLightContainer,
				expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') &&
					psaSpotLightContainer,
			]}
		>
			<Checkbox
				isDisabled={isLockedOut || isSharingToEmailsDisabled}
				isChecked={isChecked}
				label={formatMessage(getCheckboxLabel(checkboxLabel))}
				onChange={() => onNotifyCheckboxClick(isChecked)}
			/>
			{!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') && (
				<Pressable
					xcss={addMessageButtonStyling}
					onClick={onNotifyAddMessageClick}
					isDisabled={!isChecked || isSharingToEmailsDisabled}
					aria-expanded={shouldExpandIcon}
				>
					<Flex as="span" alignItems="center">
						<Text
							size="small"
							color={
								isChecked && !isSharingToEmailsDisabled
									? 'color.text.subtlest'
									: 'color.text.disabled'
							}
						>
							{formatMessage(i18n.addMessage)}
						</Text>
						<Box xcss={iconContainer}>{getChevronIcon()}</Box>
					</Flex>
				</Pressable>
			)}
		</Stack>
	);
};

export const CtaNotifyMessage = ({
	shouldShowCtaButtons,
	natureOfCta,
	notifyCheckbox,
	checkboxLabel,
	isLockedOut,
	isCtaInProgress,
	messageComposer,
	sharingType,
	onCtaClick,
	onCancelClick,
	onNotifyCheckboxClick,
	onNotifyAddMessageClick,
	isSpecificAccessDetailsVisible,
	isLimitExceeded,
	//Specifically for GasV3 events
	pageMode,
	isDraft,
	isLivePage,
	isSharingToEmailsDisabled,
	contentId,
	hasGroups,
	hasTeams,
	hasUsers,
	includeInvites,
	generalAccessLevel,
	principalsCount,
}: CtaNotifyMessageProps) => {
	const showNotifyCheckbox = notifyCheckbox !== 'hidden';
	const showMessageComposer = messageComposer !== undefined;
	const charLimitExceeded = showMessageComposer && messageComposer.notifyMessage.length > 500;
	const ctaOptions: { [key in Exclude<NatureOfCta, 'none'>]: MessageDescriptor } = {
		share: i18n.shareButton,
		save: i18n.saveButton,
	};
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentType] = useContentType();

	const fireSubmitShareAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				actionSubjectId: 'submitShare',
				action: 'clicked',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					buttonLabel: natureOfCta,
					isNotifyCheckboxShown: showNotifyCheckbox,
					isNotify: notifyCheckbox,
					isMessagePresent: Boolean(messageComposer?.notifyMessage),
					contentType,
					contentId,
					pageMode,
					isDraft,
					isLivePage,
					hasGroups,
					hasTeams,
					hasUsers,
					includeInvites,
					generalAccessLevel,
					principalsCount,
				},
			},
		}).fire();

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'shared',
				actionSubject: 'page',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					buttonLabel: natureOfCta,
					isNotifyCheckboxShown: showNotifyCheckbox,
					isNotify: notifyCheckbox,
					isMessagePresent: Boolean(messageComposer?.notifyMessage),
					includeInvites,
				},
			},
		}).fire();
	}, [
		contentType,
		contentId,
		createAnalyticsEvent,
		natureOfCta,
		notifyCheckbox,
		showNotifyCheckbox,
		messageComposer,
		pageMode,
		isDraft,
		isLivePage,
		hasGroups,
		hasTeams,
		hasUsers,
		includeInvites,
		generalAccessLevel,
		principalsCount,
	]);

	const fireApplyRestrictionsAnalytics = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'applyRestrictions',
				action: 'applied',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					buttonLabel: natureOfCta,
					isNotifyCheckboxShown: showNotifyCheckbox,
					isNotify: notifyCheckbox,
					isMessagePresent: Boolean(messageComposer?.notifyMessage),
					contentType,
					pageMode,
					isDraft,
					isLivePage,
					contentId,
					hasGroups,
					hasTeams,
					hasUsers,
					includeInvites,
					generalAccessLevel,
					principalsCount,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		natureOfCta,
		notifyCheckbox,
		showNotifyCheckbox,
		messageComposer,
		contentType,
		pageMode,
		isDraft,
		isLivePage,
		contentId,
		hasGroups,
		hasTeams,
		hasUsers,
		includeInvites,
		generalAccessLevel,
		principalsCount,
	]);

	const handleCtaClick = useCallback(() => {
		if (sharingType === 'sharing-and-applying-restrictions') {
			fireSubmitShareAnalytics();
			fireApplyRestrictionsAnalytics();
		}

		if (sharingType === 'sharing') {
			fireSubmitShareAnalytics();
		}

		if (sharingType === 'applying-restrictions') {
			fireApplyRestrictionsAnalytics();
		}

		onCtaClick();
	}, [sharingType, onCtaClick, fireApplyRestrictionsAnalytics, fireSubmitShareAnalytics]);

	const handleCancelClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				actionSubject: 'button',
				actionSubjectId: 'CancelButton',
				action: 'clicked',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				},
			},
		}).fire();

		onCancelClick();
	}, [createAnalyticsEvent, onCancelClick]);

	const buttonsOnlyVisible = !showNotifyCheckbox;
	const buttonsCheckboxVisible = showNotifyCheckbox && !showMessageComposer;
	const buttonsCheckboxComposerVisible = showNotifyCheckbox && showMessageComposer;
	const buttonsSpecificAccessDetailsVisible = buttonsOnlyVisible && isSpecificAccessDetailsVisible;
	const buttonsCheckboxSpecificAccessDetailsVisible =
		buttonsCheckboxVisible && isSpecificAccessDetailsVisible;
	const buttonsCheckboxComposerSpecifiAccessDetailsVisible =
		buttonsCheckboxComposerVisible && isSpecificAccessDetailsVisible;

	return (
		<>
			{natureOfCta !== 'none' && (
				<Flex
					direction={showMessageComposer ? 'column' : 'row'}
					gap={showMessageComposer ? 'space.200' : 'space.100'}
					alignItems="center"
					justifyContent={showNotifyCheckbox ? 'space-between' : 'end'}
					xcss={[
						containerBaseStyling,
						buttonsOnlyVisible && withButtonsAdjustments,
						buttonsCheckboxVisible && withButtonsCheckboxAdjustments,
						buttonsCheckboxComposerVisible && withButtonsCheckboxComposerAdjustments,
						buttonsSpecificAccessDetailsVisible && withButtonsSpecificAccessDetails,
						buttonsCheckboxSpecificAccessDetailsVisible &&
							withButtonsCheckboxAndSpecificAccessDetails,
						buttonsCheckboxComposerSpecifiAccessDetailsVisible &&
							withButtonsCheckboxComposerAndSpecificAccessDetails,
						// eslint-disable-next-line confluence-feature-gating/no-preconditioning
						expValEquals('persistence_specific_access_gate', 'cohort', 'experiment') &&
							buttonsCheckboxComposerVisible &&
							withPSAButtonsCheckboxComposerAdjustments,
					]}
				>
					{showNotifyCheckbox && showMessageComposer && (
						<Stack grow="fill" space="space.100">
							<NotifyCheckboxComponent
								isLockedOut={isLockedOut}
								notifyCheckbox={notifyCheckbox}
								checkboxLabel={checkboxLabel}
								onNotifyCheckboxClick={onNotifyCheckboxClick}
								onNotifyAddMessageClick={onNotifyAddMessageClick}
								showMessageComposer={showMessageComposer}
								isSharingToEmailsDisabled={isSharingToEmailsDisabled}
							/>
							{messageComposer && <MessageComposer {...messageComposer} />}
						</Stack>
					)}
					{!showMessageComposer && showNotifyCheckbox && (
						<NotifyCheckboxComponent
							isLockedOut={isLockedOut}
							notifyCheckbox={notifyCheckbox}
							checkboxLabel={checkboxLabel}
							onNotifyCheckboxClick={onNotifyCheckboxClick}
							onNotifyAddMessageClick={onNotifyAddMessageClick}
							showMessageComposer={showMessageComposer}
							isSharingToEmailsDisabled={isSharingToEmailsDisabled}
						/>
					)}
					{shouldShowCtaButtons ? (
						<Inline
							space="space.100"
							xcss={[showMessageComposer && ctaButtonContainerStylingWithMessageComposer]}
						>
							<Button appearance="subtle" onClick={handleCancelClick} isDisabled={isLockedOut}>
								{formatMessage(i18n.cancelButton)}
							</Button>
							<Button
								appearance="primary"
								onClick={handleCtaClick}
								isLoading={isCtaInProgress}
								isDisabled={
									(!isCtaInProgress && isLockedOut) ||
									charLimitExceeded ||
									isLimitExceeded ||
									isSharingToEmailsDisabled
								}
							>
								{formatMessage(ctaOptions[natureOfCta])}
							</Button>
						</Inline>
					) : null}
					<ShortcutListener
						accelerator={['command+enter', 'ctrl+enter']}
						listener={handleCtaClick}
						allowInDialog
					/>
				</Flex>
			)}
		</>
	);
};
